/** GERP 클레임 마감(186) — 레거시 appr_edit_forms_return_tax_month_mstr */
export const CLAIM_CLOSING_TYPE = 186;

export const isClaimClosingType = (apvType: number): boolean =>
  apvType === CLAIM_CLOSING_TYPE;

export const CLAIM_CLOSING_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

export type Value = string | number | null | undefined;

/** GERP 미상신 결재 건(RO_INV) — 키는 GERP 응답 그대로 */
export type ClaimClosingCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
};

/**
 * 송장 1건(ESPIVHD). 클레임 금액 2개는 API 가 화면용 음수로 줌(레거시 *-1).
 * 청구일자·결재일자·발주구분·마감형태는 스웨거에 없음 — 오면 표시
 */
export type ClaimClosingRow = {
  invId?: Value;
  invNo?: Value;
  brandKor?: Value;
  reqYmd?: Value;
  settlementYmd?: Value;
  grTypKor?: Value;
  supplyKor?: Value;
  vdCd?: Value;
  vdNm?: Value;
  totInvAmt?: Value;
  vatAmt?: Value;
  supplyAmt?: Value;
  claimSapAmtKrw?: Value;
  claimAmtKrw?: Value;
};

export type ClaimClosingCard = {
  aprvId?: string | null;
  docTit?: string | null;
  rows?: ClaimClosingRow[] | null;
};

/**
 * 상세보기 송장 라인(ESPIVDT). chk 1=라인, 2=INV_NO 소계.
 * CURRENCY·단가(통화)·청구금액(통화)·부가세·환율·결제방법은 스웨거에 없음 — 오면 표시
 */
export type ClaimClosingDetailLine = {
  chk?: Value;
  invNo?: Value;
  invLno?: Value;
  orderNo?: Value;
  cur?: Value;
  itemQty?: Value;
  itemPrice?: Value;
  unitPriceCurr?: Value;
  supplyAmt?: Value;
  grAmtCurr?: Value;
  vatAmt?: Value;
  exchRate?: Value;
  settlementKor?: Value;
};

export type ClaimClosingDetail = {
  aprvId?: string | null;
  lines?: ClaimClosingDetailLine[] | null;
};

export type ClaimClosingDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: ClaimClosingCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: ClaimClosingCard[] | null } | null;
    detail: ClaimClosingDetail | null;
  };
};

/** 작성 중 고른 건 1개 */
export type ClaimClosingItem = {
  /** GERP 결재 건(ESGMSTR.APRV_ID) */
  aprvId: string;
  /** 레거시 data-add_doctit — 건 제목(DOC_TIT) */
  docTit: string;
  rows: ClaimClosingRow[];
};

/** 저장 attributes 와 같은 모양 */
export type ClaimClosingState = {
  systemId: string;
  approvalTypeCode: string;
  /** 고른 건, 쉼표로 이음 */
  approvalId: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

/** 레거시 콤보 문구 — 건 제목(DOC_TIT) */
export const buildClaimClosingLabel = (c: ClaimClosingCandidate) =>
  c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 건 기준, "외" 앞 공백 없음 */
export const buildClaimClosingSubject = (
  docTits: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!docTits.length) return '';
  const [first] = docTits;
  if (docTits.length === 1) return first;
  return t('{{docTit}}(외 {{count}}건)', {
    docTit: first,
    count: docTits.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildClaimClosingEditorContent = (
  docTits: string[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가')} : ${docTits.join(
    ',',
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseClaimClosingAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ClaimClosingState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    systemId: nonEmpty(attributes.systemId) ?? '',
    approvalTypeCode: nonEmpty(attributes.approvalTypeCode) ?? '',
    approvalId: nonEmpty(attributes.approvalId) ?? '',
    content,
  };
};

export const parseClaimClosingContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildClaimClosingAttributes = (
  state: ClaimClosingState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

/** 레거시는 임시저장·상신 모두 카드가 없으면 막음 */
export const checkClaimClosingBeforeSave = (
  state: ClaimClosingState | null | undefined,
): string | null =>
  state?.content && state.approvalId ? null : CLAIM_CLOSING_MISSING_MESSAGE;

export const CLAIM_CLOSING_REJECT_REASON_MAX_LENGTH = 300;

export const claimClosingKey = (state: ClaimClosingState | null | undefined) =>
  state?.approvalId ?? '';
